<template lang="pug">
.vue-app-date-picker.reports-app-datepicker.gap-10(:class="{ disabled }")
  a.prev.btn.btn-default.btn-icon(
    v-show="showQuickPick"
    v-tooltip="{ placement: 'top', title: () => previousDisabledTooltip }"
    href="#"
    data-container="body"
    data-testid="date-picker-prev-button"
    :disabled="previousDisabled ? 'disabled' : null"
    @click.prevent="gotoPreviousDate"
  )
    i.hi.hi-left-arrow.text-lighter
  a.next.btn.btn-default.btn-icon(
    v-show="showQuickPick"
    v-tooltip="{ placement: 'top', title: () => nextDisabledTooltip }"
    href="#"
    data-container="body"
    data-testid="date-picker-next-button"
    :disabled="nextDisabled ? 'disabled' : null"
    @click.prevent="gotoNextDate"
  )
    i.hi.hi-right-arrow.text-lighter
  a(
    v-tooltip="inputTooltip ? { placement: 'top', title: () => inputTooltip } : false"
    :class="'datepicker ' + extraPickerClasses"
    href="#"
    data-testid="datepicker-weekly-range-input"
    @click.prevent=""
  )
    input.form-control(
      :class="extraInputClasses"
      :data-testid="inputTestId"
      data-auto-update-input="false"
      :data-date="datepickerFormattedValue"
      :data-max-date="formatDate(maxDate)"
      :data-min-date="formatDate(minDate)"
      :data-time-data-dates="timeDataDates"
      :data-start-week-on="startWeekOn"
      :value="date"
      :disabled="disabled"
      :readonly="readonly"
      :data-no-clear="noClear"
    )
  a.today.btn.btn-default.xs-half-col.margin-bottom-sm.js-filter-date-actions(
    v-if="!hideToday"
    v-show="showQuickPick"
    href="#"
    @click.prevent="gotoCurrent"
  ) Today
</template>

<script>
import { formatDate, parseInternalDate, createWithZone } from '~/utils/time_utils';
import { isEmpty, isNil } from 'lodash-es';
import VModelCompat from '~/mixins/v_model_compat';

export default {
  name: 'AppDatePicker',
  mixins: [VModelCompat],
  props: {
    modelValue: {
      type: Object,
    },
    type: {
      type: String,
      // supported: 'daily', 'weekly' and 'monthly'.
      default: 'weekly',
      validator: (value) => ['daily', 'weekly', 'monthly'].includes(value),
    },
    showQuickPick: {
      type: Boolean,
      default: true,
    },
    // This prop is related to the "gotoNextDate" arrow only.
    // It does NOT disable future dates in the calendar.
    // For disabling future dates we need to use the maxDate props.
    disableFuture: {
      type: Boolean,
      default: false,
    },
    hideToday: {
      type: Boolean,
      default: false,
    },
    startDate: {},
    endDate: {},
    minDate: {},
    maxDate: {},
    timeDataDates: [],
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    extraPickerClasses: {
      type: String,
      default: 'weekly-range-input',
    },
    extraInputClasses: {
      type: String,
    },
    inputTestId: {
      type: String,
      default: null,
    },
    // Tooltip for the date field only, so hovering the arrows doesn't open it.
    inputTooltip: {
      type: String,
      default: null,
    },
    minDateTooltip: {
      type: String,
      default: 'Cannot go past minimum date',
    },
    startWeekOn: {
      default: 0,
    },
    noClear: {
      type: Boolean,
      default: true,
    },
    direction: {
      type: String,
      default: 'left',
      validator: (value) => ['left', 'center', 'right'].includes(value),
    },
    async: {
      type: Boolean,
      default: false,
    },
    currentZone: {
      type: Object,
      required: false,
      default() {
        return { name: moment.tz.guess() };
      },
    },
  },
  data() {
    return {
      dateRangePicker: null,
      selectedDate: null,
    };
  },
  computed: {
    datepickerFormattedValue() {
      if (isNil(this.modelCompatValue)) {
        return;
      }

      return this.selectedDate || this.modelCompatValue._i;
    },
    daily() {
      return this.type === 'daily';
    },
    minimumDate() {
      if (isEmpty(this.minDate)) {
        return;
      }

      return moment(this.modelCompatValue).tz
        ? createWithZone(this.minDate, null, moment(this.modelCompatValue).tz())
        : moment(this.minDate);
    },
    startDateOfWeek() {
      const startOfWeek = toMomentIsoWeekday(this.startWeekOn);

      return moment(this.modelCompatValue).startOfIsoWeek(startOfWeek);
    },
    stopDateOfWeek() {
      const startOfWeek = toMomentIsoWeekday(this.startWeekOn);

      return moment(this.modelCompatValue).endOfIsoWeek(startOfWeek);
    },
    previousDisabled() {
      let disabled;

      if (!isEmpty(this.minDate)) {
        let date = this.daily ? this.modelCompatValue : this.stopDateOfWeek;

        date = moment(date).subtract(1, this.period);
        disabled = this.isBeforeMinDate(date);
      }

      return disabled;
    },
    previousDisabledTooltip() {
      return this.previousDisabled ? this.minDateTooltip : null;
    },
    nextDisabled() {
      let disabled;

      if (this.disableFuture) {
        let date = this.daily ? this.modelCompatValue : this.startDateOfWeek;

        date = date.clone().add(1, this.period);
        disabled = this.isFuture(date);
      }

      return disabled;
    },
    nextDisabledTooltip() {
      let tooltip;

      if (this.nextDisabled) {
        tooltip = this.daily ? 'Date is in the future' : 'Date range is in the future';
      }

      return tooltip;
    },
    period() {
      return this.daily ? 'day' : 'week';
    },
    previousDate() {
      return this.modelCompatValue.subtract(1, this.period);
    },
    nextDate() {
      return this.modelCompatValue.add(1, this.period);
    },
    now() {
      return moment();
    },
    date() {
      if (this.type === 'weekly') {
        const date = this.startDateOfWeek;
        const startDate = formatDate(date);

        if (this.endDate) {
          const endDate = formatDate(date.add(6, 'day'));

          return `${startDate} - ${endDate}`;
        }

        return startDate;
      }

      if (this.type === 'monthly') {
        const start = moment(this.startDate).startOf('month');
        const stop = moment(start).endOf('month');

        return `${formatDate(start)} - ${formatDate(stop)}`;
      }

      const date = parseInternalDate(this.modelCompatValue);

      return formatDate(date);
    },
  },
  watch: {
    maxDate(newDate) {
      this.dateRangePicker.maxDate = moment.tz(newDate, this.currentZone.name || this.currentZone.for);
    },
    modelCompatValue() {
      if (this.daily) {
        this.initialize();
      }
    },
  },
  async mounted() {
    if (this.async) {
      await new Promise((resolve) => setTimeout(resolve, 200));
    }

    this.initialize();
  },
  methods: {
    async initialize() {
      await this.$nextTick();

      const $el = $(this.$el);
      const pickers = setupDatePickers($el, {
        opens: this.direction,
        parentEl: this.$el,
        onchange: ($picker, start, end) => {
          this.gotoDate(start);
        },
        zone: this.currentZone.name || this.currentZone.for,
      });

      if (pickers?.length) {
        const picker = pickers[0];

        this.dateRangePicker = picker.data('daterangepicker');

        if (this.daily && this.modelCompatValue) {
          this.resetDate(this.modelCompatValue);
        }

        picker.on('cancel.daterangepicker', () => {
          this.emitModelCompatValue(null);
          this.gotoDate(null);
        });
        picker.on('show.daterangepicker', () => this.analyticsTrack('Date opened'));

        // If those values are not defined we don't move the calendar
        if (isNil(this.startDate) || isNil(this.endDate)) {
          return;
        }

        /**
         * Move the calendar to the month that the current
         * date range (this.startDate - this.stopDate) is within.
         **/
        const value = this.selectedDate || this.modelCompatValue;

        if (value && (this.startDate?.isAfter?.(value) || this.endDate?.isBefore?.(value))) {
          this.dateRangePicker.setStartDate(this.startDate);
          this.dateRangePicker.setEndDate(this.endDate);
          this.resetDate(this.startDate);
        }
      }
    },
    isFuture(date) {
      return date.isAfter(this.now);
    },
    isBeforeMinDate(date) {
      return date.isBefore(this.minimumDate, 'day');
    },
    gotoDate(date) {
      if (!date) {
        return false;
      }

      this.emitModelCompatValue(date);
    },
    gotoPreviousDate() {
      if (!this.previousDisabled) {
        this.gotoDate(this.previousDate);
        this.analyticsTrack('Date arrow selected', { direction: 'backward' });
      }
    },
    gotoNextDate() {
      if (!this.nextDisabled) {
        this.gotoDate(this.nextDate);
        this.analyticsTrack('Date arrow selected', { direction: 'forward' });
      }
    },
    gotoCurrent() {
      const date = moment();

      this.gotoDate(date);
    },
    resetDate(date) {
      this.dateRangePicker.setStartDate(date);
      this.dateRangePicker.setEndDate(formatDate(date));
    },
    analyticsTrack(eventName, eventProperties) {
      window.analytics?.track(eventName, { type: 'simple', ...eventProperties });
    },
    formatDate(date) {
      return date && moment(date).format('YYYY-MM-DD');
    },
  },
};
</script>
